// INTELIMOVE-XPR-005 (IM-ADR-016, IM-UX-118) — Comparação SEMÂNTICA entre a versão confirmada do contexto da empresa e o
// estado atual. Puro: sem banco, sem rede, sem relógio. É a mesma regra no servidor (alerta "há alterações não confirmadas",
// recusa de versão sem mudança) e na tela (revisão antes de confirmar).
// O que NÃO é mudança de negócio: ordem de listas, espaços, maiúsculas/minúsculas e forma Unicode, vazio × nulo × lista
// vazia, metadados técnicos (versão do item, execução, evidências, confiança e justificativa da pesquisa, datas, ordem de
// exibição, esquema do snapshot). O que É: valor de campo de negócio, inclusão, remoção, arquivamento, reativação,
// situação do concorrente e relação entre itens (oferta × ICP, persona e concorrente).

export type ContextChangeKind = 'INCLUSAO' | 'REMOCAO' | 'ALTERACAO' | 'ARQUIVAMENTO' | 'REATIVACAO' | 'RELACAO';
export type ContextChangeSection = 'profile' | 'offerings' | 'icps' | 'personas' | 'competitors';

export const CONTEXT_CHANGE_KIND_LABEL: Record<ContextChangeKind, string> = {
  INCLUSAO: 'Inclusão', REMOCAO: 'Remoção', ALTERACAO: 'Alteração', ARQUIVAMENTO: 'Arquivamento', REATIVACAO: 'Reativação', RELACAO: 'Relação alterada',
};
export const CONTEXT_CHANGE_SECTION_LABEL: Record<ContextChangeSection, string> = {
  profile: 'Dados da empresa', offerings: 'Produtos e serviços', icps: 'Perfis de cliente (ICPs)', personas: 'Personas', competitors: 'Concorrentes',
};
/** Efeito de cada seção nas próximas análises (linguagem de negócio). */
export const CONTEXT_CHANGE_IMPACT: Record<ContextChangeSection, string> = {
  profile: 'Muda a descrição da empresa usada nas análises de mercado, nos battlecards e nas comparações.',
  offerings: 'Muda o portfólio usado na análise de portfólio, nas comparações dos battlecards e na aderência das empresas-alvo.',
  icps: 'Muda os perfis usados para avaliar a aderência das empresas-alvo, a segmentação e o mapeamento das ofertas.',
  personas: 'Muda os decisores usados para localizar pessoas nas empresas-alvo.',
  competitors: 'Muda o conjunto de concorrentes usado nos battlecards, no posicionamento e no monitoramento.',
};

export interface ContextChange {
  section: ContextChangeSection;
  entityId: string | null;
  /** Nome do item afetado (oferta, ICP, persona, concorrente); nulo nos dados da empresa. */
  entityName: string | null;
  field: string | null;
  fieldLabel: string | null;
  kind: ContextChangeKind;
  /** Valores já formatados para leitura ("sem valor" quando vazio). */
  before: string | null;
  after: string | null;
}

type Obj = Record<string, unknown>;
/** Snapshot mínimo que a comparação precisa (o `ContextSnapshot` imutável e o montado agora). */
export interface ComparableSnapshot {
  profile: Obj;
  offerings: Obj[]; icps: Obj[]; personas: Obj[]; competitors: Obj[];
}
export interface DiffOptions {
  /** Situação ATUAL dos itens ausentes do snapshot atual (arquivado → "Arquivamento"; senão "Remoção"). */
  currentStatus?: Readonly<Record<string, string>>;
  /** Itens que já existiam na confirmação (presentes agora e ausentes da versão = "Reativação"; senão "Inclusão"). */
  existedAtConfirmation?: ReadonlySet<string>;
}

export const PROFILE_FIELD_LABEL: Record<string, string> = {
  legalName: 'Razão social', tradeName: 'Nome comercial', shortDescription: 'Descrição curta', longDescription: 'Descrição completa', taxId: 'CNPJ',
  country: 'País', region: 'Estado', city: 'Cidade', language: 'Idioma', currency: 'Moeda', timezone: 'Fuso horário', primaryDomain: 'Site principal',
  additionalDomains: 'Outros domínios', sector: 'Setor', subsectors: 'Subsetores', businessModel: 'Modelo de negócio', customerType: 'Tipo de cliente',
  companySize: 'Porte', markets: 'Mercados', servedRegions: 'Regiões atendidas', salesChannels: 'Canais de venda', differentiators: 'Diferenciais',
  commercialGoals: 'Objetivos comerciais', commercialConstraints: 'Restrições comerciais', foundedOn: 'Fundação', legalNature: 'Natureza jurídica',
  registrationStatus: 'Situação cadastral', shareCapital: 'Capital social', cnaePrimary: 'CNAE principal', cnaeSecondary: 'CNAEs secundários',
  employeeRange: 'Faixa de funcionários', headquarters: 'Sede', address: 'Endereço', postalCode: 'CEP', phone: 'Telefone', email: 'E-mail',
  socialProfiles: 'Perfis sociais', logoUrl: 'Logotipo', languages: 'Idiomas', geographicScope: 'Abrangência geográfica',
};
/** Campos de NEGÓCIO por tipo de item (o resto do snapshot é metadado). `offeringIds` é relação. */
export const ENTITY_FIELDS: Record<Exclude<ContextChangeSection, 'profile'>, Record<string, string>> = {
  offerings: { name: 'Nome', kind: 'Tipo', description: 'Descrição', problemsSolved: 'Problemas resolvidos', benefits: 'Benefícios', targetAudience: 'Público declarado',
    regions: 'Regiões', segments: 'Segmentos', audienceScope: 'Público', audienceNote: 'Observação sobre o público' },
  icps: { name: 'Nome', isGeneral: 'Perfil geral', description: 'Descrição', segments: 'Segmentos', companySizes: 'Portes', locations: 'Localizações', maturity: 'Maturidade',
    technologies: 'Tecnologias', pains: 'Dores', needs: 'Necessidades', triggers: 'Gatilhos', blockers: 'Impeditivos', positiveCriteria: 'Critérios positivos', negativeCriteria: 'Critérios negativos' },
  personas: { name: 'Nome', equivalentTitles: 'Cargos equivalentes', department: 'Área', seniority: 'Senioridade', buyingRole: 'Papel na compra', influence: 'Influência',
    pains: 'Dores', goals: 'Objetivos', objections: 'Objeções', offeringIds: 'Ofertas relacionadas' },
  competitors: { name: 'Nome', website: 'Site', reason: 'Motivo', notes: 'Notas', status: 'Situação', offeringIds: 'Ofertas relacionadas' },
};
const VALUE_LABEL: Record<string, string> = {
  PRODUCT: 'Produto', SERVICE: 'Serviço', SOLUTION: 'Solução', B2B: 'B2B', B2C: 'B2C', MIXED: 'Misto', TRANSVERSAL: 'Transversal ou institucional', SEM_PUBLICO: 'Sem público definido',
  CONFIRMED: 'confirmado', HYPOTHESIS: 'hipótese', RELATED: 'relacionado', DISCARDED: 'descartado', ARCHIVED: 'arquivado',
};

/** Texto comparável: forma Unicode, espaços e caixa não contam. */
export const normText = (v: unknown): string => String(v ?? '').normalize('NFC').replace(/\s+/g, ' ').trim().toLocaleLowerCase('pt-BR');
const isEmpty = (v: unknown) => v === null || v === undefined || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);
/** Valor canônico para comparação: listas viram conjunto ordenado; vazio, nulo e lista vazia são o mesmo. */
export function canonicalValue(v: unknown): string {
  if (isEmpty(v)) return '';
  if (Array.isArray(v)) return JSON.stringify([...new Set(v.map((x) => (x && typeof x === 'object' ? JSON.stringify(x) : normText(x))).filter(Boolean))].sort());
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') return JSON.stringify(Object.keys(v as Obj).sort().map((k) => [k, canonicalValue((v as Obj)[k])]));
  return normText(v);
}
export const sameValue = (a: unknown, b: unknown) => canonicalValue(a) === canonicalValue(b);

function show(v: unknown, names?: ReadonlyMap<string, string>): string {
  if (isEmpty(v)) return 'sem valor';
  if (Array.isArray(v)) return v.map((x) => (names?.get(String(x)) ?? VALUE_LABEL[String(x)] ?? String(x))).join(', ');
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  return VALUE_LABEL[String(v)] ?? String(v);
}
const nameOf = (e: Obj) => String(e.name ?? e.id ?? '');

/** Diferenças semânticas entre a versão confirmada (`base`) e o estado atual (`current`). Ordem: seção, item, campo. */
export function diffContextSnapshots(base: ComparableSnapshot, current: ComparableSnapshot, opts: DiffOptions = {}): ContextChange[] {
  const out: ContextChange[] = [];
  for (const [k, label] of Object.entries(PROFILE_FIELD_LABEL)) {
    const a = base.profile?.[k], b = current.profile?.[k];
    if (!sameValue(a, b)) out.push({ section: 'profile', entityId: null, entityName: null, field: k, fieldLabel: label, kind: 'ALTERACAO', before: show(a), after: show(b) });
  }
  // Nomes de oferta (das duas versões) para exibir relações em linguagem de negócio.
  const offeringNames = new Map<string, string>([...base.offerings, ...current.offerings].map((o) => [String(o.id), nameOf(o)]));
  for (const section of ['offerings', 'icps', 'personas', 'competitors'] as const) {
    const fields = ENTITY_FIELDS[section];
    const A = new Map(base[section].map((e) => [String(e.id), e]));
    const B = new Map(current[section].map((e) => [String(e.id), e]));
    for (const [id, a] of A) {
      const b = B.get(id);
      if (!b) {
        const archived = opts.currentStatus?.[id] === 'ARCHIVED';
        out.push({ section, entityId: id, entityName: nameOf(a), field: null, fieldLabel: null, kind: archived ? 'ARQUIVAMENTO' : 'REMOCAO', before: section === 'competitors' ? show(a.status) : 'confirmado', after: archived ? 'arquivado' : opts.currentStatus?.[id] ? show(opts.currentStatus[id]) : 'fora do contexto' });
        continue;
      }
      for (const [f, label] of Object.entries(fields)) {
        // A relação oferta × ICP é mostrada do ponto de vista da oferta (abaixo), como no mapeamento.
        if (section === 'icps' && f === 'offeringIds') continue;
        if (sameValue(a[f], b[f])) continue;
        out.push({ section, entityId: id, entityName: nameOf(b), field: f, fieldLabel: label, kind: f === 'offeringIds' ? 'RELACAO' : 'ALTERACAO',
          before: f === 'offeringIds' && isEmpty(a[f]) ? 'sem oferta relacionada' : show(a[f], offeringNames), after: f === 'offeringIds' && isEmpty(b[f]) ? 'sem oferta relacionada' : show(b[f], offeringNames) });
      }
    }
    for (const [id, b] of B) {
      if (A.has(id)) continue;
      const back = !!opts.existedAtConfirmation?.has(id);
      out.push({ section, entityId: id, entityName: nameOf(b), field: null, fieldLabel: null, kind: back ? 'REATIVACAO' : 'INCLUSAO', before: back ? 'fora do contexto' : null, after: section === 'competitors' ? show(b.status) : 'confirmado' });
    }
  }
  // Relação oferta × ICP, por oferta: "Testes de Performance — Antes: sem ICP relacionado · Agora: Operações digitais…".
  const icpLinks = (s: ComparableSnapshot) => {
    const m = new Map<string, Set<string>>();
    for (const i of s.icps) for (const o of (i.offeringIds as string[] | undefined) ?? []) (m.get(o) ?? m.set(o, new Set()).get(o)!).add(String(i.id));
    return m;
  };
  const icpNames = new Map<string, string>([...base.icps, ...current.icps].map((i) => [String(i.id), nameOf(i)]));
  const LA = icpLinks(base), LB = icpLinks(current);
  const currentOfferings = new Set(current.offerings.map((o) => String(o.id)));
  const baseOfferings = new Set(base.offerings.map((o) => String(o.id)));
  for (const oid of new Set([...LA.keys(), ...LB.keys()])) {
    if (!currentOfferings.has(oid) || !baseOfferings.has(oid)) continue; // inclusão/remoção da oferta já foi dita acima
    const a = [...(LA.get(oid) ?? [])], b = [...(LB.get(oid) ?? [])];
    if (sameValue(a, b)) continue;
    const fmt = (xs: string[]) => (xs.length ? xs.map((x) => icpNames.get(x) ?? x).sort().join(', ') : 'sem ICP relacionado');
    out.push({ section: 'offerings', entityId: oid, entityName: offeringNames.get(oid) ?? oid, field: 'icps', fieldLabel: 'ICPs relacionados', kind: 'RELACAO', before: fmt(a), after: fmt(b) });
  }
  const order: ContextChangeSection[] = ['profile', 'offerings', 'icps', 'personas', 'competitors'];
  return out.sort((x, y) => order.indexOf(x.section) - order.indexOf(y.section) || (x.entityName ?? '').localeCompare(y.entityName ?? '', 'pt-BR'));
}

/** Seções afetadas, com quantidade e efeito nas análises (para o resumo da revisão). */
export function changeSections(changes: readonly ContextChange[]): Array<{ section: ContextChangeSection; label: string; count: number; impact: string }> {
  const by = new Map<ContextChangeSection, number>();
  for (const c of changes) by.set(c.section, (by.get(c.section) ?? 0) + 1);
  return [...by.entries()].map(([section, count]) => ({ section, label: CONTEXT_CHANGE_SECTION_LABEL[section], count, impact: CONTEXT_CHANGE_IMPACT[section] }));
}

/**
 * Estado do contexto em linguagem de tela (IM-UX-118): quatro situações distintas, nunca misturadas.
 *   INCOMPLETO — falta mínimo obrigatório (CTA "Completar contexto", com o que falta);
 *   SUGESTOES — mínimo atendido, há sugestões da pesquisa por decidir;
 *   ALTERADO — mínimo atendido e há diferença semântica para a versão confirmada (CTA "Revisar alterações");
 *   CONFIRMADO — mínimo atendido, sem diferença semântica (nenhum CTA de confirmação).
 * Sem versão confirmada ainda e com mínimo atendido = PRIMEIRA_CONFIRMACAO.
 */
export type ContextSituation = 'INCOMPLETO' | 'SUGESTOES' | 'ALTERADO' | 'CONFIRMADO' | 'PRIMEIRA_CONFIRMACAO' | 'PESQUISANDO';
export function contextSituation(x: { ready: boolean; status: string; currentVersion: number | null; semanticChanges: number; pendingSuggestions: number }): ContextSituation {
  if (x.status === 'ENRICHING') return 'PESQUISANDO';
  if (!x.ready) return 'INCOMPLETO';
  if (!x.currentVersion) return 'PRIMEIRA_CONFIRMACAO';
  if (x.semanticChanges > 0) return 'ALTERADO';
  if (x.pendingSuggestions > 0) return 'SUGESTOES';
  return 'CONFIRMADO';
}
export const CONTEXT_SITUATION_LABEL: Record<ContextSituation, string> = {
  INCOMPLETO: 'Contexto incompleto', SUGESTOES: 'Confirmado · sugestões aguardando revisão', ALTERADO: 'Completo, com alterações ainda não confirmadas',
  CONFIRMADO: 'Completo e confirmado', PRIMEIRA_CONFIRMACAO: 'Completo · falta a primeira confirmação', PESQUISANDO: 'Pesquisando o site',
};

/** Resposta de IM-API-328 `GET /api/v1/company-context/changes`. */
export interface ContextChangeDetailDto extends ContextChange {
  /** Origem da última mudança registrada no item (pessoa, pesquisa, módulo de concorrentes…), autor e data — quando houver. */
  origin: string | null; author: string | null; at: string | null;
}
export interface ContextChangesDto {
  baseVersion: number | null; baseConfirmedAt: string | null;
  changes: ContextChangeDetailDto[];
  sections: Array<{ section: ContextChangeSection; label: string; count: number; impact: string }>;
  /** Pode virar nova versão agora (mínimo atendido, sem pesquisa em andamento, com diferença real). */
  canConfirm: boolean; blockedReason: string | null;
}
